import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Input,
  VStack,
  Text,
  HStack,
  Badge,
  useColorModeValue,
  useOutsideClick,
} from "@chakra-ui/react";
import PhoneStatusBadge from "./sms/PhoneStatusBadge";
import { getSupabase } from "./supabaseClient";
import { prompts, tableName, getBackendURL } from "./env";
import { isInGroup, hasClinicalAdminPermissions } from "../config/roles";
import { sharedUsersOverlapOrClauses } from "../utils/shareAccess";
import { useSelectedProviders } from "../hooks/useSelectedProviders";
import {
  normalizeSuggestionRow,
  coalesceSuggestionContact,
} from "../utils/rosterPatientFields";
import {
  isCutoverEffective, startSuggestionsFromRds, serveSuggestionsFromRds,
  startSuggestionVisitsFromRds, serveSuggestionVisitsFromRds,
} from "../utils/rdsCutover";

// RDS cutover flag / dashboard pilot for the roster half of the
// suggestions (utils/rdsCutover.js, TEST_INTEGRATION_PLAN.md §6).
const SUGGEST_CALL_SITE = "patient_suggest";
// Roster rows fetched per schema. Passed to RDS too so both sides answer
// the same question.
const ROSTER_SUGGEST_LIMIT = 10;

// Function to format DOB from yyyy-mm-dd to mm/dd/yyyy
const formatDOB = (dobValue) => {
  if (!dobValue) return "N/A";

  // Check if it's already in mm/dd/yyyy format
  if (dobValue.includes("/")) {
    return dobValue;
  }

  // Check if it's in yyyy-mm-dd format
  if (dobValue.includes("-")) {
    try {
      // Parse the date components directly to avoid timezone issues
      const [year, month, day] = dobValue.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      if (isNaN(date.getTime())) {
        return dobValue; // Return original if invalid date
      }
      
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.warn("Error formatting DOB:", error);
      return dobValue; // Return original if error
    }
  }

  return dobValue; // Return original if not in expected format
};

// Function to format date for display without timezone issues
const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return "Unknown";
  
  try {
    // If it's already in yyyy-mm-dd format, parse it directly to avoid timezone issues
    if (dateValue.includes("-")) {
      const [year, month, day] = dateValue.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      
      if (isNaN(date.getTime())) {
        return dateValue; // Return original if invalid date
      }
      
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    }
    
    // If it's already a formatted date string, return as is
    return dateValue;
  } catch (error) {
    console.warn("Error formatting date for display:", error);
    return dateValue; // Return original if error
  }
};

const PatientSuggestionDropdown = ({
  value,
  onChange,
  onPatientSelect,
  placeholder = "Search for existing patients...",
  disabled = false,
  entityName = "Patient",
  // "Last visit" is the latest visit STRICTLY BEFORE this date (the schedule
  // date the caller is on). Defaults to today when not provided.
  referenceDate = null,
}) => {
  // Cross-schema providers: suggestions span the WHOLE clinic group, not the
  // Providers dropdown's current selection. Looking up an existing patient
  // before creating one is a de-duplication step — missing a chart because a
  // provider happened to be unticked on Schedules is how the same person ends
  // up entered twice under two doctors. `allSchemas` is the group (it falls
  // back to users.schema when the home schema is ungrouped).
  const { allSchemas, homeSchema } = useSelectedProviders();
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  // Search generation: a keystroke's search that resolves AFTER a newer
  // one started must not overwrite the newer suggestions. Supabase alone
  // answered uniformly enough for this to go unnoticed; with RDS in the
  // path (its own timeout, its own latency) it is enforced, and the
  // superseded RDS request is aborted rather than left to finish.
  const searchGenRef = useRef(0);
  const searchAbortRef = useRef(null);

  // Color mode values
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  // Debounce search to avoid excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        searchPatients(searchQuery);
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Search for patients in the database
  const searchPatients = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    // Reference date for "last visit" — visits on/after it (e.g. future
    // scheduled appointments) are NOT counted. Defaults to today.
    const refDate = referenceDate || new Date().toISOString().split("T")[0];

    searchGenRef.current += 1;
    const myGen = searchGenRef.current;
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const abort = new AbortController();
    searchAbortRef.current = abort;

    setIsLoading(true);
    try {
      const supabase = await getSupabase();
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Error getting session:", sessionError);
        return;
      }

      const userId = sessionData?.session?.user?.id;
      const userEmail = sessionData?.session?.user?.email;

      if (!userId) {
        console.error("User ID not found in session.");
        return;
      }

      // Get user role
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("role")
        .eq("email", userEmail)
        .single();

      if (userError) {
        console.error("Error fetching user role:", userError);
        return;
      }

      const role = userData.role;
      const nameFieldKey = prompts[0][0]; // Get the name field key

      // Access-control owner-ids resolved ONCE, reused for every schema.
      let ownerIds = [];
      if (role !== "admin" && role !== "doctor" && isInGroup(role, 'tenantSchema')) {
        try {
          const backendUrl = getBackendURL();
          const res = await fetch(`${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`);
          const json = await res.json().catch(() => ({}));
          if (res.ok && Array.isArray(json.owner_ids)) ownerIds = json.owner_ids;
        } catch (_) {}
      }

      // The same access filter the single-schema query applied, per schema.
      const applyAccess = (q) => {
        if (role === "admin" || role === "doctor") return q;
        if (isInGroup(role, 'tenantSchema')) {
          const orParts = [
            `user_id.eq.${userId}`,
            ...sharedUsersOverlapOrClauses(userId),
          ];
          if (ownerIds.length > 0) {
            orParts.push(`user_id.in.(${ownerIds.join(",")})`);
          }
          return q.or(orParts.join(","));
        }
        return q.eq("user_id", userId);
      };

      // Cross-schema: search every schema in the group. The roster
      // (patients_with_ehrid) is a clinical surface, so it's only searched for
      // clinical roles — matching the roster page's own gate.
      const schemas = allSchemas.length ? allSchemas : [homeSchema];
      const includeRoster = hasClinicalAdminPermissions(role);
      // Phase 4 cutover, site 2 (utils/rdsCutover.js `patient_suggest`):
      // when both switches are on, the roster half of each schema's
      // suggestions is SERVED by RDS while the Supabase read below still
      // runs as the parity oracle and the fallback. Off (the default, and
      // whenever RDS says the flag is not effective) this is byte-for-byte
      // the old behaviour. The visits half (notes-c) is untouched.
      const cutoverOn = includeRoster && await isCutoverEffective(SUGGEST_CALL_SITE);
      if (searchGenRef.current !== myGen) return;

      const perSchema = await Promise.all(
        schemas.map(async (s) => {
          const client = s === homeSchema ? supabase : supabase.schema(s);
          // Visits (notes-c) — access-filtered. Two reads so "last visit" is the
          // latest visit BEFORE refDate even when a patient has many future rows,
          // while a patient whose only rows are future is still discoverable:
          //   (a) latest visits strictly before refDate;  (b) any recent visits.
          //
          // Phase 4 cutover, site 7 (utils/rdsCutover.js `suggestion_visits`):
          // when both switches are on, each variant is dual-read -- RDS is
          // asked the same question in parallel with the Supabase read,
          // Supabase stays as the parity oracle and the fallback. Off
          // (the default, and whenever RDS says the flag is not
          // effective) this is byte-for-byte the old behaviour.
          const visitsP = (async () => {
            const pastVisitsCutover = cutoverOn
              ? startSuggestionVisitsFromRds(s, { query, refDate, includeFuture: false, signal: abort.signal })
              : null;
            const anyVisitsCutover = cutoverOn
              ? startSuggestionVisitsFromRds(s, { query, refDate, includeFuture: true, signal: abort.signal })
              : null;
            try {
              const nameMatch = () =>
                applyAccess(
                  client.from(tableName).select("*").ilike(nameFieldKey, `%${query}%`)
                );
              const [pastRows, anyRows] = await Promise.all([
                (async () => {
                  // eslint-disable-next-line no-await-in-loop
                  const r = await nameMatch().lt("date_", refDate).order("date_", { ascending: false }).limit(15);
                  let rows = r.data || [];
                  if (r.error) console.warn(`[suggestions] ${s}.${tableName} (past):`, r.error.message);
                  if (pastVisitsCutover) {
                    // eslint-disable-next-line no-await-in-loop
                    const served = await serveSuggestionVisitsFromRds(s, {
                      query, refDate, includeFuture: false, supabaseRows: rows, started: pastVisitsCutover,
                    });
                    rows = served.rows;
                  }
                  return rows;
                })(),
                (async () => {
                  // eslint-disable-next-line no-await-in-loop
                  const r = await nameMatch().order("date_", { ascending: false, nullsFirst: false }).limit(15);
                  let rows = r.data || [];
                  if (r.error) console.warn(`[suggestions] ${s}.${tableName} (any):`, r.error.message);
                  if (anyVisitsCutover) {
                    // eslint-disable-next-line no-await-in-loop
                    const served = await serveSuggestionVisitsFromRds(s, {
                      query, refDate, includeFuture: true, supabaseRows: rows, started: anyVisitsCutover,
                    });
                    rows = served.rows;
                  }
                  return rows;
                })(),
              ]);
              return [...(pastRows || []), ...(anyRows || [])].map((r) => ({ ...r, _schema: s }));
            } catch (e) {
              console.warn(`[suggestions] ${s}.${tableName} threw:`, e?.message);
              return [];
            }
          })();
          // Roster (patients_with_ehrid) — patient-level rows, not user-scoped.
          const rosterP = (async () => {
            if (!includeRoster) return [];
            // Started BEFORE the Supabase read so the two round trips
            // overlap; settled after it, with Supabase's rows in hand.
            const rdsStarted = cutoverOn
              ? startSuggestionsFromRds(s, { query, limit: ROSTER_SUGGEST_LIMIT, signal: abort.signal })
              : null;
            try {
              // Ordered by name so the 10 rows are the same 10 on every
              // read — and the same 10 RDS is asked for (parity needs both
              // sides to answer the same question).
              const { data, error } = await client
                .from("patients_with_ehrid")
                .select("*")
                .ilike("patient_name", `%${query}%`)
                .order("patient_name", { ascending: true, nullsFirst: false })
                .limit(ROSTER_SUGGEST_LIMIT);
              if (error) {
                console.warn(`[suggestions] ${s}.patients_with_ehrid:`, error.message);
                return [];
              }
              let rows = data || [];
              if (rdsStarted) {
                // RDS's answer (its own row-level authorization applied) is
                // what the user sees; any RDS failure resolves to the
                // Supabase rows already fetched. Either way it is reported.
                const served = await serveSuggestionsFromRds(s, {
                  query, supabaseRows: rows, started: rdsStarted,
                });
                rows = served.rows;
              }
              return rows.map((r) => ({ ...r, _schema: s, _source: "roster" }));
            } catch (e) {
              console.warn(`[suggestions] ${s}.patients_with_ehrid threw:`, e?.message);
              return [];
            }
          })();
          const [visits, roster] = await Promise.all([visitsP, rosterP]);
          // Visits first so a real visit wins the dedupe over the roster row —
          // it carries date_/time_/type that the quick-add auto-fill wants.
          return [...visits, ...roster];
        })
      );

      // Group by name+schema: collapses a patient's many visits (and their
      // roster row) WITHIN a schema, while keeping same-named patients from
      // DIFFERENT providers visible. `lastVisit` is the latest visit BEFORE
      // refDate across their fetched rows (future / on-date rows don't count),
      // and the representative row (quick-add auto-fill) is that latest prior
      // visit. A qualifying dated visit always beats a dateless roster row.
      //
      // Every row is kept, not just the representative: contact details are
      // entered inconsistently (a phone on one visit, an email on the next, a
      // portal signup on the roster row with neither), so collapsing to one row
      // used to throw away details that had already been fetched. The rows
      // arrive visits-first and newest-first, which is the fallback order
      // coalesceSuggestionContact wants.
      const byKey = new Map();
      perSchema.flat().forEach(patient => {
        const patientName = patient[nameFieldKey] || patient.patient_name;
        if (!patientName) return;
        const key = `${patientName.toLowerCase()}|${patient._schema}`;
        // Roster rows speak a different dialect (ehr_id / phone_numbers) and
        // carry columns notes-c doesn't have. Translate before anything
        // downstream treats this as a visit row.
        const row = normalizeSuggestionRow(patient);
        // Only a visit strictly BEFORE refDate counts as a "last visit".
        const d = patient.date_ && patient.date_ < refDate ? patient.date_ : null;
        const existing = byKey.get(key);
        if (!existing) {
          byKey.set(key, { rep: row, name: patientName, latestDate: d, rows: [row] });
          return;
        }
        existing.rows.push(row);
        if (d && (!existing.latestDate || d > existing.latestDate)) {
          existing.latestDate = d;
          existing.rep = row;
        }
      });

      const uniquePatients = Array.from(byKey.values()).map(({ rep, name, latestDate, rows }) => ({
        ...coalesceSuggestionContact(rep, rows),
        displayName: name,
        lastVisit: latestDate ? formatDateForDisplay(latestDate) : 'Unknown',
        visitCount: 1, // We could calculate this if needed
      }));

      if (searchGenRef.current !== myGen) return;
      setSuggestions(uniquePatients);
      setIsOpen(uniquePatients.length > 0);
    } catch (error) {
      console.error("Error in searchPatients:", error);
    } finally {
      // A superseded search leaves the spinner to the search that replaced it.
      if (searchGenRef.current === myGen) setIsLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
  };

  // Handle patient selection
  const handlePatientSelect = (patient) => {
    setSearchQuery(patient.displayName);
    onChange(patient.displayName);
    setIsOpen(false);
    if (onPatientSelect) {
      onPatientSelect(patient);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  // Handle outside click
  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsOpen(false),
  });

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <Box position="relative" ref={dropdownRef}>
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={1000}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
          maxH="300px"
          overflowY="auto"
          mt={1}
        >
          {isLoading ? (
            <Box p={3} textAlign="center">
              <Text color="gray.500">Searching...</Text>
            </Box>
          ) : suggestions.length > 0 ? (
            <VStack spacing={0} align="stretch">
              {suggestions.map((patient, index) => (
                <Box
                  key={patient.id}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() => handlePatientSelect(patient)}
                  borderBottom={index < suggestions.length - 1 ? "1px solid" : "none"}
                  borderBottomColor={borderColor}
                >
                  <HStack justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack spacing={2} align="center">
                        <Text fontWeight="semibold" fontSize="sm">
                          {patient.displayName}
                        </Text>
                        {/* Cross-schema: label a suggestion pulled from another
                            provider's schema so it's clear it isn't yours. */}
                        {patient._schema && homeSchema && patient._schema !== homeSchema && (
                          <Badge bg="#e0e7ff" color="#4338ca" fontSize="0.6rem">
                            {patient._schema}
                          </Badge>
                        )}
                      </HStack>
                      <HStack spacing={2} wrap="wrap">
                        {patient.email && (
                          <Badge colorScheme="blue" size="sm">
                            {patient.email}
                          </Badge>
                        )}
                        {patient.tel_num && (
                          <Badge colorScheme="green" size="sm">
                            {patient.tel_num}
                          </Badge>
                        )}
                        {patient.tel_num && <PhoneStatusBadge phone={patient.tel_num} compact />}
                        {String(patient.home_phone || "").trim() && (
                          <Badge colorScheme="teal" size="sm" variant="outline" title="Home phone">
                            Home: {patient.home_phone}
                          </Badge>
                        )}
                        {String(patient.home_phone || "").trim() && (
                          <PhoneStatusBadge phone={patient.home_phone} compact />
                        )}
                        {patient.dob && (
                          <Badge colorScheme="purple" size="sm">
                            DOB: {formatDOB(patient.dob)}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="xs" color="gray.500">
                        Last {entityName === "Patient" ? "visit" : "entry"}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {patient.lastVisit}
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : searchQuery.trim().length >= 2 ? (
            <Box p={3} textAlign="center">
              <Text color="gray.500">No {entityName.toLowerCase()}s found</Text>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default PatientSuggestionDropdown;
